import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { MdOutlineFilterAlt } from "react-icons/md";
import { debounce } from "lodash";
import NoDataFound from "../../Common/NoDataFound";
import useNetworkServices from "../../../Networking/baseAPIManager";
import MerchantCard from "../../Common/Cards/MerchantCard";
import Dropdown from "../../Common/Dropdown";
import SubHeaderMenu from "../../Sidebar/SubHeaderMenu";
import Loader from "../../Common/Loader";

const MerchantOverviewCards = () => {
  const { merchantList } = useNetworkServices();
  const location = useLocation();
  const [merchantListData, setMerchantListData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [filterStatus, setFilterStatus] = useState(""); // Active/Inactive filter status
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const navigate = useNavigate();

  const option = [
    { value: "filter", name: "All" },
    { value: "true", name: "Active" },
    { value: "false", name: "Inactive" },
  ];

  // Function to fetch merchant data
  const merchantListDetails = () => {
    setLoading(true);
    merchantList()
      .then((response) => {
        const data = response?.data?.data || [];
        setMerchantListData(data);
        setFilteredData(data); // Initially set filtered data as the full list
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Fetch data whenever `refresh` is toggled
  useEffect(() => {
    merchantListDetails();
    setRefresh(false);
  }, [refresh]);

  // Handle both filtering (status and search query)
  useEffect(() => {
    let filtered = merchantListData;

    // Apply search filter on shop name, location, and pincode
    if (searchQuery.trim()) {
      const lowerCaseQuery = searchQuery.toLowerCase();
      filtered = filtered.filter((merchant) => {
        // Check if any of the search fields match
        return (
          merchant?.businessDetils?.shopName
            ?.toLowerCase()
            .includes(lowerCaseQuery) ||
          merchant?.businessDetils?.businessAddress
            ?.toLowerCase()
            .includes(lowerCaseQuery) ||
          merchant?.businessDetils?.businessPinCode
            ?.toString()
            .includes(lowerCaseQuery)
        );
      });
    }

    // Apply status filter
    if (filterStatus === "true") {
      filtered = filtered.filter((merchant) => merchant.is_active);
    } else if (filterStatus === "false") {
      filtered = filtered.filter((merchant) => !merchant.is_active);
    }

    setFilteredData(filtered);
  }, [merchantListData, filterStatus, searchQuery]);

  // Debounced search handler to avoid frequent state updates
  const handleSearch = debounce((query) => {
    setSearchQuery(query);
  }, 100);

  return (
    <div id="main" className="p-4">
      <h1 className="font-bold text-2xl text-slate-900 pb-5">
        <span>Merchant Management</span> /{" "}
        <span className="text-red-500">
          <SubHeaderMenu location={location} />
        </span>
      </h1>

      <div className="w-full flex justify-between items-center pe-5 py-5">
        <div className="flex items-center w-1/2">
          <input
            type="text"
            placeholder="Search by shop name, location, or pincode..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none"
          />
        </div>
        <div className="w-32 h-auto flex justify-center items-center border-[1px] bg-[#025D89] rounded-lg py-1 gap-1">
          <MdOutlineFilterAlt className="text-white" />
          <Dropdown
            option={option}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            SelectClass="bg-[#025D89] text-white outline-none"
          />
        </div>
      </div>

      <div className="grid xl:grid-cols-4 lg:grid-cols-3 md:grid-cols-2 gap-3">
        {filteredData.length === 0 ? (
          <NoDataFound message="No merchants found" />
        ) : (
          <MerchantCard data={filteredData} setRefresh={setRefresh} />
        )}
      </div>
      {loading && <Loader />}
    </div>
  );
};

export default MerchantOverviewCards;
